import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaBuilding,
  FaChartLine,
  FaEnvelope,
  FaLock,
  FaShieldAlt,
  FaUsers,
} from 'react-icons/fa';
import axios from 'axios';
import { getApiUrl } from '../../config/api';
import { registerPushNotifications } from '../../services/pushNotificationService';
import { syncWorkspaceAccessFromResponse } from '../../utils/workspaceSession';
import './WorkspaceJoinPage.css';

const OWNER_ONLY_MESSAGE =
  'This portal is reserved for Company Owners. Please contact your company owner to receive an invitation.';

const OWNER_FEATURES = [
  { icon: FaUsers, label: 'Manage staff & invite codes' },
  { icon: FaChartLine, label: 'Analytics & revenue dashboard' },
  { icon: FaShieldAlt, label: 'Full administrative control' },
];

const WorkspaceLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      const role = String(user?.role || '');
      if (role === 'company_admin') {
        navigate('/workspace', { replace: true });
        return;
      }
      if (role === 'company_manager' || role === 'company_staff') {
        setError(OWNER_ONLY_MESSAGE);
      }
    } catch {
      /* ignore */
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password) {
      setError('Email and password are required');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(getApiUrl('/api/auth/workspace-owner-login'), {
        email: email.trim(),
        password,
      });

      if (res.data.success && res.data.token) {
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        syncWorkspaceAccessFromResponse(res.data);
        window.dispatchEvent(new CustomEvent('codex-authenticated'));
        await registerPushNotifications({ requestPermission: true }).catch(() => {});
        navigate('/workspace', { replace: true });
      } else {
        setError(res.data.message || 'Sign in failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ws-join-page">
      <div className="ws-join-bg" aria-hidden="true">
        <span className="ws-join-orb ws-join-orb-1" />
        <span className="ws-join-orb ws-join-orb-2" />
        <span className="ws-join-orb ws-join-orb-3" />
      </div>

      <div className="ws-join-shell">
        <aside className="ws-join-aside">
          <div className="ws-join-brand">
            <span className="ws-join-brand-icon">
              <FaBuilding />
            </span>
            <div>
              <p className="ws-join-brand-label">UBSAAPP Workspace</p>
              <h2 className="ws-join-brand-title">Company Owner Portal</h2>
            </div>
          </div>

          <p className="ws-join-aside-lead">
            Sign in with the email used when you registered your company. Manage staff, properties,
            bookings, and workspace settings from one dashboard.
          </p>

          <ul className="ws-join-steps">
            {OWNER_FEATURES.map(({ icon: Icon, label }) => (
              <li key={label}>
                <span className="ws-join-step-icon">
                  <Icon />
                </span>
                {label}
              </li>
            ))}
          </ul>

          <div className="ws-join-trust">
            <FaShieldAlt />
            <span>Only registered company owners may sign in here. Staff use an invite link.</span>
          </div>
        </aside>

        <main className="ws-join-card">
          <header className="ws-join-card-header">
            <h1>Owner Login</h1>
            <p>Access your company workspace with full administrative privileges.</p>
          </header>

          {error && (
            <div className="ws-join-alert" role="alert">
              {error}
            </div>
          )}

          <form className="ws-join-form" onSubmit={handleSubmit}>
            <div className="ws-join-field">
              <label htmlFor="ws-owner-email">Owner email</label>
              <div className="ws-join-input-wrap">
                <FaEnvelope className="ws-join-input-icon" />
                <input
                  id="ws-owner-email"
                  className="ws-join-input"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  placeholder="owner@company.com"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="ws-join-field">
              <label htmlFor="ws-owner-password">Password</label>
              <div className="ws-join-input-wrap">
                <FaLock className="ws-join-input-icon" />
                <input
                  id="ws-owner-password"
                  className="ws-join-input"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                  placeholder="Your password"
                  required
                  autoComplete="current-password"
                />
              </div>
            </div>

            <button type="submit" className="ws-join-submit" disabled={loading}>
              {loading ? (
                <>
                  <span className="ws-join-spinner" aria-hidden="true" />
                  Signing in…
                </>
              ) : (
                'Sign in to Workspace'
              )}
            </button>
          </form>

          <p className="ws-join-footer">
            Staff or manager?{' '}
            <Link to="/join-workspace">Join with your invite code</Link>
          </p>
        </main>
      </div>
    </div>
  );
};

export default WorkspaceLoginPage;
